import { Link } from '@tanstack/react-router';
import { ChevronDown, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { rankFlaggedAgents } from './agent-category-charts';
import { AgentCategoryCharts } from './components/AgentCategoryCharts';
import { DebugSkeletonSheet } from './components/DebugSkeletonSheet';
import { DeleteIssuesDialog } from './components/DeleteIssuesDialog';
import { IssueFilters } from './components/IssueFilters';
import { IssuesTable } from './components/IssuesTable';
import {
  hasIssueFilters,
  useDeleteOnlineEvalIssues,
  useIssueCategories,
  useOnlineEvalEvaluations,
  useOnlineEvalAgentCounts,
  useOnlineEvalIssues,
} from './online-eval.query';

import type {
  OnlineEvalChartSettings,
  OnlineEvalIssueFilters,
} from './online-eval.types';

const PAGE_SIZE = 100;

type OnlineEvalIssuesPageProps = {
  filters: OnlineEvalIssueFilters;
  chartSettings: OnlineEvalChartSettings;
  debugSkeletonIssueId: string | null;
  onFiltersChange: (filters: OnlineEvalIssueFilters) => void;
  onChartSettingsChange: (settings: OnlineEvalChartSettings) => void;
  onDebugSkeletonIssueIdChange: (issueId: string | null) => void;
};

export const OnlineEvalIssuesPage = ({
  filters,
  chartSettings,
  debugSkeletonIssueId,
  onFiltersChange,
  onChartSettingsChange,
  onDebugSkeletonIssueIdChange,
}: OnlineEvalIssuesPageProps) => {
  const categoriesQuery = useIssueCategories();
  const evaluationsQuery = useOnlineEvalEvaluations();
  const issuesQuery = useOnlineEvalIssues(filters);
  const agentCountsQuery = useOnlineEvalAgentCounts(filters);
  const deleteIssues = useDeleteOnlineEvalIssues();
  const toast = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const data = issuesQuery.data;
  const matchingCount = data?.total ?? 0;
  const filtered = hasIssueFilters(filters);
  const pageCount = Math.max(1, Math.ceil(matchingCount / PAGE_SIZE));

  const openDeleteDialog = () => {
    deleteIssues.reset();
    setDeleteDialogOpen(true);
  };
  const confirmDelete = () =>
    deleteIssues.mutate(filters, {
      onSuccess: ({ deleted_count }) => {
        setDeleteDialogOpen(false);
        toast.success(
          'Findings deleted',
          `Deleted ${deleted_count.toLocaleString()} findings.`
        );
        onFiltersChange({ ...filters, page: 1 });
      },
    });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-primary">Online eval</p>
          <h1 className="text-3xl font-semibold tracking-tight">
            Flagged issues
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Review evaluator findings and operational metadata. Conversation
            content stays unavailable, except in your own chats.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/online-eval/categories">Manage categories</Link>
        </Button>
      </div>

      <IssueFilters
        filters={filters}
        categories={categoriesQuery.data ?? []}
        evaluations={evaluationsQuery.data ?? []}
        agents={
          agentCountsQuery.data ? rankFlaggedAgents(agentCountsQuery.data) : []
        }
        models={data?.filter_options.models ?? []}
        onChange={onFiltersChange}
        actions={
          <details className="group">
            <summary className="flex cursor-pointer list-none items-center justify-end gap-1 text-xs text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden">
              More actions
              <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
            </summary>
            <div className="mt-2 flex flex-col items-end gap-1">
              <Button
                variant="destructive"
                size="sm"
                disabled={
                  issuesQuery.isPlaceholderData ||
                  matchingCount === 0 ||
                  !filtered
                }
                onClick={openDeleteDialog}
              >
                <Trash2 className="mr-1 h-4 w-4" /> Delete matching findings…
              </Button>
              {!filtered && (
                <span className="text-xs text-muted-foreground">
                  Set at least one filter to delete findings
                </span>
              )}
            </div>
          </details>
        }
      />

      <AgentCategoryCharts
        counts={agentCountsQuery.data ?? null}
        loading={agentCountsQuery.isPending}
        stale={agentCountsQuery.isPlaceholderData}
        error={agentCountsQuery.error?.message ?? null}
        agentFilter={filters.agents}
        categoryIdFilter={filters.categoryIds}
        settings={chartSettings}
        onSettingsChange={onChartSettingsChange}
      />

      {issuesQuery.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          Loading findings…
        </div>
      ) : issuesQuery.error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {issuesQuery.error.message}
        </div>
      ) : (
        <div className="space-y-2">
          {issuesQuery.isPlaceholderData && (
            <span
              role="status"
              className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-foreground"
            >
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Loading the new filters or page; the table below shows the
              previous ones
            </span>
          )}
          <div
            aria-busy={issuesQuery.isPlaceholderData}
            className={cn(
              'transition-opacity',
              issuesQuery.isPlaceholderData && 'opacity-40'
            )}
          >
            <p className="mb-2 text-sm text-muted-foreground">
              {matchingCount.toLocaleString()} findings match the filters
            </p>
            <IssuesTable
              issues={data?.issues ?? []}
              onViewDebugSkeleton={onDebugSkeletonIssueIdChange}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Page {filters.page} of {pageCount}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={filters.page <= 1}
            onClick={() =>
              onFiltersChange({ ...filters, page: filters.page - 1 })
            }
          >
            Previous
          </Button>
          <Button
            variant="outline"
            disabled={filters.page >= pageCount}
            onClick={() =>
              onFiltersChange({ ...filters, page: filters.page + 1 })
            }
          >
            Next
          </Button>
        </div>
      </div>
      <DeleteIssuesDialog
        open={deleteDialogOpen}
        filters={filters}
        categories={categoriesQuery.data ?? []}
        matchingCount={matchingCount}
        pending={deleteIssues.isPending}
        error={deleteIssues.error?.message ?? null}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDelete}
      />
      <DebugSkeletonSheet
        issueId={debugSkeletonIssueId}
        open={debugSkeletonIssueId !== null}
        onOpenChange={(open) => {
          if (!open) onDebugSkeletonIssueIdChange(null);
        }}
      />
    </div>
  );
};
